import { queryAlgolia } from '../../../util/api';
import { algoliaListingFacets } from '../../../config/configDefault';
import { parse } from '../../../util/urlHelpers';
// algoliasearch is loaded lazily (see util/algoliaClient) so it is not shipped in
// the main bundle on every page — it is only needed when an Algolia request runs.
import { getAlgoliaClient } from '../../../util/algoliaClient';
import { facetLabelMappings } from '../../../util/constants';
import { createBoundingBox } from '../../../util/data';
import {
  buildAvailabilityFilter,
  getAvailabilityRange,
  isAvailableForRange,
} from '../../../util/availability';


// ================ Action types ================ //

export const SEARCH_MAP_SET_ACTIVE_LISTING =
  'app/SearchPage/SEARCH_MAP_SET_ACTIVE_LISTING';

export const FACETS_REQUEST = 'app/SearchPage/FACETS_REQUEST';
export const FACETS_SUCCESS = 'app/SearchPage/FACETS_SUCCESS';
export const FACETS_ERROR = 'app/SearchPage/FACETS_ERROR';

export const FACETS_SORTINGS_REQUEST = 'app/SearchPage/FACETS_SORTINGS_REQUEST';
export const FACETS_SORTINGS_SUCCESS = 'app/SearchPage/FACETS_SORTINGS_SUCCESS';
export const FACETS_SORTINGS_ERROR = 'app/SearchPage/FACETS_SORTINGS_ERROR';

export const ALGOLIA_QUERY_REQUEST = 'app/SearchPage/ALGOLIA_QUERY_REQUEST';
export const ALGOLIA_QUERY_SUCCESS = 'app/SearchPage/ALGOLIA_QUERY_SUCCESS';
export const ALGOLIA_QUERY_ERROR = 'app/SearchPage/ALGOLIA_QUERY_ERROR';

export const ALGOLIA_QUERY_LISTINGS_SUCCESS = 'app/SearchPage/ALGOLIA_QUERY_LISTINGS_SUCCESS';
export const ALGOLIA_QUERY_STORES_SUCCESS = 'app/SearchPage/ALGOLIA_QUERY_STORES_SUCCESS';


const ALGOLIA_CLIENT_ID = process.env.REACT_APP_ALGOLIA_APP_ID;
const ALGOLIA_API_KEY = process.env.REACT_APP_ALGOLIA_ADMIN_API_KEY;
const ALGOLIA_PRIME_LISTINGS_INDEX = process.env.REACT_APP_ALGOLIA_LISTINGS_FAMILY_INDEX;
// const ALGOLIA_USERS_INDEX = process.env.REACT_APP_ALGOLIA_USERS_INDEX;

function buildAlgoliaFilters(params) {
  const {
    ageSuitability,
    price,
    bedsCounts,
    Bedrooms,
    Beds,
    Bathrooms,
    Amenities,
    availabilityStartUnix,
    availabilityEndUnix,
    pets_allowed,
    maximumGuests,
  } = params;
  const filtersArray = [];
  if (maximumGuests) {
    filtersArray.push(`publicData.maximumGuests:${maximumGuests}`);
  }
  if (price) {
    filtersArray.push(`price.amount:${price}`);
  }
  // "Available for the whole selected range". This is a superset filter -
  // hits still go through the exact per-block check in isAvailableForRange().
  const availabilityFilter = buildAvailabilityFilter(
    availabilityStartUnix,
    availabilityEndUnix
  );
  if (availabilityFilter) {
    filtersArray.push(availabilityFilter);
  }
  if (bedsCounts) {
    const bedsCountsList = bedsCounts.split(',');
    bedsCountsList.forEach(bedCount => {
      filtersArray.push(`publicData.bedsCounts.name:${bedCount.trim()}`);
    });
  }
  if (Bathrooms) {
    filtersArray.push(`publicData.NumberofBathrooms:${Bathrooms}`);
  }
  if (Bedrooms) {
    filtersArray.push(`publicData.NumberofBedrooms:${Bedrooms}`);
  }
  if (Beds) {
    filtersArray.push(`publicData.beds:${Beds}`);
  }
  if (Amenities) {
    const amenitiesList = Amenities.split(',');
    amenitiesList.forEach(amenity => {
      filtersArray.push(`publicData.amenities:${amenity.trim()}`);
    });
  }
  if (pets_allowed) {
    filtersArray.push(`publicData.pets_allowed:${pets_allowed}`);
  }

  // Join the filters with AND condition for Algolia
  return filtersArray.join(' AND ');
};

function getLabeledFacets(facets) {
  const labeledFacets = {};

  // Loop through each facet category in the facets object
  for (const facetCategory in facets) {

    // Remove "publicData." prefix if present
    const facetKey = facetCategory.replace("publicData.", "");

    // Find the correct label mapping for the facet key
    const labelMapping = facetLabelMappings[facetKey];

    if (labelMapping) {
      labeledFacets[facetKey] = Object.keys(facets[facetCategory]).map(facetValue => ({
        value: facetValue,
        label: labelMapping[facetValue] || facetValue, // Fallback to the raw value if no label is found
        count: facets[facetCategory][facetValue],
      }));
    } else {
      // If there's no mapping, just return the raw facet values and counts
      labeledFacets[facetKey] = Object.keys(facets[facetCategory]).map(facetValue => ({
        value: facetValue,
        label: facetValue, // Use raw value as label
        count: facets[facetCategory][facetValue],
      }));
    }
  }

  return labeledFacets;
}

export const PRICE_FACET = 'price.amount';

const coverEarthRadius = 20037500;

// Each page load runs two queries against the same slice of the store: a fast
// 24-hit one and a 150-hit follow-up fired 2s later. Both are keyed to the
// search they were built from, so that a response belonging to a superseded
// search (e.g. the one from before a price range was applied) can be dropped
// instead of overwriting the current, correctly filtered results.
let currentSearchKey = null;
let followUpTimeoutId = null;

// ================ Reducer ================ //

const initialState = {
  activeListingId: null,
  // facets is an object keyed by facet category (see getLabeledFacets), not an array.
  facets: {},
  facetsInProgress: false,
  facetsError: null,
  sorting: [],
  sortingInProgress: false,
  sortingError: null,
  algoliaQueryInProgress: false,
  algoliaQueryError: null,
  algoliaListings: [],
  algoliaStores: [],
  nbHitsInsideBounds: 0,
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_MAP_SET_ACTIVE_LISTING:
      return {
        ...state,
        activeListingId: payload,
      };

    case FACETS_REQUEST:
      return {
        ...state,
        facetsInProgress: true,
        facetsError: null,
      };
    case FACETS_SUCCESS:
      return {
        ...state,
        facetsInProgress: false,
        facets: payload,
      };
    case FACETS_ERROR:
      return {
        ...state,
        facetsInProgress: false,
        facetsError: payload
      };

    case FACETS_SORTINGS_REQUEST:
      return {
        ...state,
        sortingInProgress: true,
        facetsError: null,
      };
    case FACETS_SORTINGS_SUCCESS:
      return {
        ...state,
        sortingInProgress: false,
        sorting: payload,
      };
    case FACETS_SORTINGS_ERROR:
      return {
        ...state,
        sortingInProgress: false,
        sortingError: payload
      };

    case ALGOLIA_QUERY_REQUEST:
      return {
        ...state,
        algoliaQueryInProgress: true,
        algoliaQueryError: null,
      };
    case ALGOLIA_QUERY_SUCCESS:
      return {
        ...state,
        algoliaQueryInProgress: false,
      };
    case ALGOLIA_QUERY_LISTINGS_SUCCESS:
      return {
        ...state,
        algoliaListings: payload.listings,
        nbHitsInsideBounds: payload.nbHitsInsideBounds
      };
    case ALGOLIA_QUERY_STORES_SUCCESS:
      return {
        ...state,
        algoliaStores: payload,
      }
    case ALGOLIA_QUERY_ERROR:
      return {
        ...state,
        algoliaQueryInProgress: false,
        algoliaQueryError: payload,
      };
    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});


// Algolia facets
export const algoliaFacetsRequest = () => ({
  type: FACETS_REQUEST
});

export const algoliaFacetsSuccess = (facets) => ({
  type: FACETS_SUCCESS,
  payload: facets
});


export const algoliaSortingsError = (err) => ({
  type: FACETS_ERROR,
  payload: err
});

// Algolia facets
export const algoliaSortingsRequest = () => ({
  type: FACETS_SORTINGS_REQUEST
});

export const algoliaSortingsSuccess = (hits) => ({
  type: FACETS_SORTINGS_SUCCESS,
  payload: hits
});

export const algoliaFacetsError = (err) => ({
  type: FACETS_SORTINGS_ERROR,
  payload: err
});

// Algolia Query
// Separate actions
const algoliaQueryListingsSuccess = ({ listings, nbHitsInsideBounds }) => ({
  type: ALGOLIA_QUERY_LISTINGS_SUCCESS,
  payload: { listings, nbHitsInsideBounds },
});

const algoliaQueryStoresSuccess = (stores) => ({
  type: ALGOLIA_QUERY_STORES_SUCCESS,
  payload: stores,
});

export const algoliaQueryRequest = () => ({
  type: ALGOLIA_QUERY_REQUEST
});

export const algoliaQuerySuccess = () => ({
  type: ALGOLIA_QUERY_SUCCESS,
});

export const algoliaQueryError = (err) => ({
  type: ALGOLIA_QUERY_ERROR,
  payload: err
});


export const getFacets = (params) => async (dispatch, getState, sdk) => {
  dispatch(algoliaFacetsRequest());
  const searchClient = await getAlgoliaClient(ALGOLIA_CLIENT_ID, ALGOLIA_API_KEY);
  const listings = ALGOLIA_PRIME_LISTINGS_INDEX;
  const listingIndex = searchClient.initIndex(listings);
  return listingIndex.search('', {
    facets: algoliaListingFacets,
  })
    .then(({ facets, facets_stats: facetsStats }) => {
      const labeledFacets = getLabeledFacets(facets);
      // price.amount is a numeric facet: the price slider needs the range the
      // listings actually span, not the value -> count list the other facets use.
      const priceStats = facetsStats?.[PRICE_FACET];
      if (priceStats) {
        labeledFacets[PRICE_FACET] = {
          min: Math.floor(priceStats.min),
          max: Math.ceil(priceStats.max),
        };
      }
      dispatch(algoliaFacetsSuccess(labeledFacets));
      return facets;
    })
    .catch(err => {
      dispatch(algoliaFacetsError(err))
    });
};

export const getSortings = (indexName) => async (dispatch, getState, sdk) => {
  dispatch(algoliaSortingsRequest());
  // Initialize Algolia client (loaded lazily)
  const searchClient = await getAlgoliaClient(ALGOLIA_CLIENT_ID, ALGOLIA_API_KEY);
  const sortedIndex = searchClient.initIndex(indexName);
  // Perform the search
  return sortedIndex.search()
    .then(({ hits }) => {
      dispatch(algoliaSortingsSuccess(hits));
      return hits;
    })
    .catch(err => {
      dispatch(algoliaSortingsError(err));
      throw err;
    });
};

export const getAlgoliaResults = (params, fetchedEntites, filterHit, isCurrent) => (dispatch, getState, sdk) => {
  dispatch(algoliaQueryRequest());
  return queryAlgolia(params)
    .then(results => {
      // The search this response was made for is no longer the one on screen
      // (e.g. the user changed a filter while it was in flight) - dispatching it
      // would show hits that don't match the current filters.
      if (isCurrent && !isCurrent()) {
        return results;
      }
      const { listings, nbHitsInsideBounds } = results.data;
      const publishedListings = listings?.filter(
        listing => listing?.state === 'published' && (!filterHit || filterHit(listing))
      );
      if (fetchedEntites.includes('listings')) {
        dispatch(algoliaQueryListingsSuccess({ listings: publishedListings, nbHitsInsideBounds }))
      };
      dispatch(algoliaQuerySuccess());
      return results;
    })
    .catch(err => {
      dispatch(algoliaQueryError(err))
    })
};

export const loadData = (params, search, config) => (

  dispatch,
  getState,
  sdk
) => {
  const { currentUserLocationInfo } = getState().user;
  const { origin: userOrigin } = currentUserLocationInfo || {};
  const { lng: userLng, lat: userLat } = userOrigin || {};
  const ottawaLatLng = [45.4215, -75.6972];
  const defaultLatLng = userLng && userLat
    ? [userLat, userLng]
    : ottawaLatLng; // Ottawa's center coordinates

  // Only the browser has a single "current search" - on the server the module
  // state is shared by every concurrent request, so every SSR response counts
  // as current.
  const isClient = typeof window !== 'undefined';
  const searchKey = search || '';
  const isCurrentSearch = () => !isClient || currentSearchKey === searchKey;

  if (isClient) {
    currentSearchKey = searchKey;
    // A follow-up query still waiting for its timer belongs to the previous
    // search - it must not fire against the filters the user just replaced.
    if (followUpTimeoutId) {
      clearTimeout(followUpTimeoutId);
      followUpTimeoutId = null;
    }
  }

  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const {
    page = 1,
    keyword = "",
    address,
    bounds,
    origin: latlng,
    maximumGuests,
    price,
    startDate,
    endDate,
    bedsCounts,
    Bathrooms,
    Amenities,
    Bedrooms,
    Beds,
    pets_allowed,
  } = queryParams;

  // Selected booking range: listings must be available for every day in it.
  const { startUnix, endUnix } = getAvailabilityRange({ startDate, endDate });
  const filterByAvailability = listing =>
    isAvailableForRange(listing, startUnix, endUnix);

  const aroundLatLngAndRadius =
    latlng && latlng.lat && latlng.lng
      ? { aroundLatLng: `${latlng.lat}, ${latlng.lng}`, aroundRadius: coverEarthRadius }
      : { aroundLatLng: `${defaultLatLng[0]}, ${defaultLatLng[1]}`, aroundRadius: coverEarthRadius };

  function normalizeLongitude(lng) {
    return ((lng + 180) % 360 + 360) % 360 - 180;
  }

  const { ne, sw } = bounds || {};

  const normalizedNELng = ne ? normalizeLongitude(ne.lng) : null;
  const normalizedSWLng = sw ? normalizeLongitude(sw.lng) : null;

  const queryBoundingBox = ne && sw
    ? [ne.lat, normalizedNELng, sw.lat, normalizedSWLng]
    : null;

  const userBoundingBox = userLat && userLng ? createBoundingBox(userLat, userLng, 100) : null;
  const ottawaBoundingBox = createBoundingBox(ottawaLatLng[0], ottawaLatLng[1], 100);

  const insideBoundingBox = queryBoundingBox || userBoundingBox || ottawaBoundingBox;

  const filters = buildAlgoliaFilters({
    maximumGuests,
    price,
    availabilityStartUnix: startUnix,
    availabilityEndUnix: endUnix,
    bedsCounts,
    Bedrooms,
    Beds,
    Bathrooms,
    Amenities,
    pets_allowed,
  });

  const listings = ALGOLIA_PRIME_LISTINGS_INDEX;
  // Fetch first 24 results (listings and stores)
  const algoliaResultsPromise = dispatch(getAlgoliaResults({
    page,
    query: keyword,
    address,
    aroundLatLngAndRadius,
    filters,
    insideBoundingBox,
    indexes: {
      listingIndex: { indexName: listings, minimumHits: 24 }, // Fetch 24 listings first
      // storeIndex: { indexName: ALGOLIA_USERS_INDEX, minimumHits: 16 } // Fetch 16 stores first
    }
  }, ['listings', 'stores'], filterByAvailability, isCurrentSearch));

  const algoliaFacetsPromise = dispatch(getFacets(params));
  // Store IDs of fetched listings to prevent refetching
  // let fetchedListingIds = [];

  algoliaResultsPromise.then((results) => {
    // Server-side rendering only needs the first batch - nothing is waiting for
    // a timer that fires 2s after the response has been sent.
    if (!isClient || !isCurrentSearch()) {
      return;
    }
    followUpTimeoutId = setTimeout(() => {
      followUpTimeoutId = null;
      if (!isCurrentSearch()) {
        return;
      }
      queryAlgolia({
        page,
        query: keyword,
        address,
        aroundLatLngAndRadius,
        filters,
        insideBoundingBox,
        indexes: {
          listingIndex: { indexName: listings, minimumHits: 150 }, // Fetch remaining listings
        },
      })
        .then(({ data }) => {
          if (!isCurrentSearch()) {
            return;
          }
          const publishedListings = data.listings?.filter(
            listing =>
              listing?.state === 'published' && filterByAvailability(listing)
          );
          dispatch(
            algoliaQueryListingsSuccess({
              listings: publishedListings,
              nbHitsInsideBounds: data.nbHitsInsideBounds,
            })
          );
        })
        .catch(err => dispatch(algoliaQueryError(err)));
    }, 2000);
  });


  return Promise.all([algoliaResultsPromise, algoliaFacetsPromise]);
};

